import { useEffect } from 'react';
import { Badge } from '../../../components/ui/Badge';
import { StateBlock } from '../../../components/ui/Feedback';
import { CloseIcon } from '../../../components/icons/Icons';
import { formatDate } from '../../../lib/format';
import type { Conversation, TrailMessage } from '../../../lib/conversations';

/** Mirrors the decision codes in `decision.ts` / `send.service.ts` (short form). */
const DECISION_LABEL: Record<string, string> = {
  ok: 'Policy checks passed',
  sending_disabled: 'Automatic sending is off',
  model_skipped: 'Draft recommended no reply',
  rate_limited_hour: 'Rate limit reached',
  rate_limited_day: 'Rate limit reached',
  rate_limited_sender_hour: 'Rate limit reached',
  rate_limited_sender_day: 'Rate limit reached',
  duplicate_reply: 'A reply already exists',
  recipient_missing: 'Sender address not usable',
  recipient_not_allowed: 'Sender address not usable',
  recipient_is_self: 'Sender address not usable',
  message_ignored: 'Message screened as unusable',
  unsupported_attachment: 'Unsupported attachment',
  dry_run_transport: 'Dry run (not sent)',
  send_unconfirmed: 'Provider did not confirm',
  provider_rejected: 'Provider did not confirm',
};

function decisionText(code: string | null): string {
  if (!code) return 'No decision recorded';
  return DECISION_LABEL[code] ?? code;
}

const TEXT_ENTITIES: Record<string, string> = {
  amp: '&',
  lt: '<',
  gt: '>',
  quot: '"',
  '#39': "'",
};

/**
 * The stored draft is the safe HTML subset used in the email; the drawer shows it as readable text
 * rather than raw tags (and never as markup).
 */
function draftText(body: string | null): string | null {
  if (!body) return null;

  return body
    .replace(/<br\s*\/?>/gi, '\n')
    .replace(/<\/(?:p|li|ul|ol)>/gi, '\n')
    .replace(/<li[^>]*>/gi, '- ')
    .replace(/<[^>]+>/g, '')
    .replace(/&(amp|lt|gt|quot|#39);/g, (match, name: string) => TEXT_ENTITIES[name] ?? match)
    .replace(/\n{3,}/g, '\n\n')
    .trim();
}

function messageClasses(message: TrailMessage, isLatest: boolean): string {
  const kindClass =
    message.kind === 'outbound'
      ? 'thread__item--outbound'
      : message.kind === 'inbound'
        ? 'thread__item--inbound'
        : 'thread__item--auto-reply';
  return ['thread__item', kindClass, isLatest ? 'conversation-drawer__message--latest' : '']
    .filter(Boolean)
    .join(' ');
}

function MailCard({ message, isLatest }: { message: TrailMessage; isLatest: boolean }) {
  const mail = message.mail;
  if (!mail) return null;

  const isOutbound = mail.direction === 'outbound';
  const who = isOutbound
    ? (mail.vendorName ?? mail.toEmail ?? 'Vendor')
    : (mail.vendorName ?? mail.fromEmail ?? 'Unknown sender');

  return (
    <article className={messageClasses(message, isLatest)}>
      <div className="thread__head">
        {isOutbound ? (
          mail.kind === 'reminder' ? (
            <Badge variant="warning">Reminder</Badge>
          ) : (
            <Badge variant="info">Sent</Badge>
          )
        ) : (
          <Badge variant="success">Reply received</Badge>
        )}
        {mail.attachmentCount > 0 ? (
          <Badge variant="neutral">{mail.attachmentCount} attachment(s)</Badge>
        ) : null}
        <span className="thread__who">{who}</span>
        <span className="thread__when">{formatDate(mail.at)}</span>
      </div>

      <p className="thread__subject">
        {mail.subject ?? '(no subject)'}
        {mail.threadToken ? <span className="thread__token">[{mail.threadToken}]</span> : null}
      </p>

      {mail.bodyPreview ? <pre className="thread__body">{mail.bodyPreview}</pre> : null}
      {mail.errorMessage ? <p className="thread__error">{mail.errorMessage}</p> : null}
    </article>
  );
}

function AutoReplyCard({ message, isLatest }: { message: TrailMessage; isLatest: boolean }) {
  const autoReply = message.autoReply;
  if (!autoReply) return null;

  const reply = autoReply.reply;
  const who = reply?.to ? `Auto-reply to ${reply.to}` : 'No recipient recorded';
  const when = formatDate(reply?.sentAt ?? reply?.createdAt ?? autoReply.receivedAt);

  return (
    <article className={messageClasses(message, isLatest)}>
      <div className="thread__head">
        <Badge variant="orange">Automated reply</Badge>
        <span className="thread__who">{who}</span>
        <span className="thread__when">{when}</span>
      </div>

      {reply?.subject ? <p className="thread__subject">{reply.subject}</p> : null}
      {reply ? <p className="thread__meta">{decisionText(reply.decisionCode)}</p> : null}
      {reply?.decisionDetail ? <p className="thread__meta">{reply.decisionDetail}</p> : null}

      {draftText(reply?.body ?? null) ? (
        <pre className="thread__body">{draftText(reply?.body ?? null)}</pre>
      ) : null}
      {reply?.bodyPurged ? (
        <p className="thread__meta">The draft text is no longer kept.</p>
      ) : null}
      {reply?.errorCode ? (
        <p className="thread__error">
          {reply.errorCode}
          {reply.errorDetail ? `: ${reply.errorDetail}` : ''}
        </p>
      ) : null}
    </article>
  );
}

export function ConversationDrawer({
  conversation,
  open,
  onClose,
}: {
  conversation: Conversation | null;
  open: boolean;
  onClose: () => void;
}) {
  useEffect(() => {
    if (!open) return undefined;
    const handleKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', handleKey);
    return () => document.removeEventListener('keydown', handleKey);
  }, [open, onClose]);

  if (!open || !conversation) return null;

  const latestId =
    conversation.messages.length > 0
      ? conversation.messages[conversation.messages.length - 1]!.id
      : null;

  return (
    <>
      <div className="conversation-drawer__backdrop" onClick={onClose} />
      <aside
        className="conversation-drawer"
        role="dialog"
        aria-modal="true"
        aria-label="Email conversation"
      >
        <div className="conversation-drawer__header">
          <div>
            <h3 className="detail-section-title">
              {conversation.vendorName ?? 'Unassigned'}
              {conversation.vendorCode ? (
                <span className="text-muted"> · {conversation.vendorCode}</span>
              ) : null}
            </h3>
            {conversation.threadToken ? (
              <p className="text-muted">[{conversation.threadToken}]</p>
            ) : null}
            {conversation.subject ? (
              <p className="text-muted">{conversation.subject}</p>
            ) : null}
          </div>
          <button
            type="button"
            className="conversation-drawer__close"
            onClick={onClose}
            aria-label="Close"
          >
            <CloseIcon />
          </button>
        </div>

        <div className="conversation-drawer__body">
          {conversation.messages.length === 0 ? (
            <StateBlock title="No messages in this conversation" />
          ) : (
            conversation.messages.map((message) =>
              message.kind === 'auto-reply' ? (
                <AutoReplyCard
                  key={message.id}
                  message={message}
                  isLatest={message.id === latestId}
                />
              ) : (
                <MailCard key={message.id} message={message} isLatest={message.id === latestId} />
              ),
            )
          )}
        </div>
      </aside>
    </>
  );
}
